import { H7 } from "components"
import { useMemo, useState } from "react"
import { Select } from "antd"
import { useGet } from "utils/useFetch"
import IndividualFlow from "../component/individual/IndividualFlow"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { useQueryClient } from "@tanstack/react-query"
import { IEmpObjRes } from "../interface/individual"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import { useSearchParams } from "react-router-dom"
import { useCycleDurationAdminStore } from "stores"
import { CustomSpin } from "components/custom/Loader"
import { IEmployeeKRListing } from "../interface/department"
import { IPeople } from "pages/dashboard/performance/types"
import KeyResultRowsList from "../component/KeyResultRowsList"

const ViewIndividual = () => {
  const selectedCDData = useCycleDurationAdminStore(
    (state) => state.selectedCDData
  )
  const selectedCycle = Number(selectedCDData?.id)
  const [openCreateCompObj, setOpenCreateCompObj] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<string>("1")
  const [activeObj, setActiveObj] = useState<number | null>(null)
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const employeeId = searchParams.get("x")
  const isPersonCentric = selectedCDData?.frameworkType === 2

  const { isLoading: isLoadingDetails, data } = useGet<IEmpObjRes>({
    url: `/api/v2/admin/okr/departmentobjectives/${employeeId}/${selectedCycle}?status=${selectedStatus}`,
    instanceKey: "newArchAxiosInstance",
    enabled: !!selectedCycle && !!employeeId && !isPersonCentric,
    queryKey: [
      "/api/v2/admin/okr/departmentobjectives",
      String(employeeId),
      String(selectedCycle),
      selectedStatus,
    ],
  })

  const { data: peopleData } = useGet<IPeople>({
    url: "/api/v2/admin/performance/people",
    instanceKey: "newArchAxiosInstance",
  })

  const { isLoading: isLoadingPersonCentricKRs, data: personCentricKRs } =
    useGet<IEmployeeKRListing>({
      url: `/api/v2/admin/okr/okrframework/employees/${employeeId}/cycledurations/${selectedCycle}/keyresults`,
      instanceKey: "newArchAxiosInstance",
      enabled: !!selectedCycle && !!employeeId && isPersonCentric,
      queryKey: [
        "/api/v2/admin/okr/okrframework/employees",
        String(employeeId),
        String(selectedCycle),
        "keyresults",
      ],
    })

  const filterOptions = [
    { label: "All", value: "0" },
    { label: "Active", value: "1" },
    { label: "Closed", value: "2" },
  ]

  const totalEmployeeTargets = useMemo(() => {
    if (isPersonCentric) {
      return personCentricKRs?.data?.length || 0
    }

    return data?.data?.reduce(
      (sum, item) => sum + (item.keyResultCount || 0),
      0
    )
  }, [data, isPersonCentric, personCentricKRs])

  const employee = peopleData?.data?.docs?.find(
    (item) => item?.personId == Number(employeeId)
  )

  return (
    <>
      <DashboardWrapper>
        <div className="flex justify-between gap-4 py-8 px-6 mb-6 bg-white rounded">
          <div className="flex items-center gap-4 ">
            <PersonAvatar
              src={employee?.avatarUrl || ""}
              className="w-14 h-14 rounded-full"
            />
            <span className="text-padeBlack font-semibold text-xl font-circular">
              {employee ? `${employee.fullName}'s` : "Employee's"} Key Results
            </span>
          </div>

          <img
            src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/d2bb9097-60ac-4f84-9f82-279c796ca376.svg"
            alt=""
            width={110}
            height={94}
          />
        </div>
        {(isPersonCentric && isLoadingPersonCentricKRs) ||
        (!isPersonCentric && isLoadingDetails) ? (
          <div className="flex justify-center items-center">
            <CustomSpin spinning />
          </div>
        ) : (
          <div className="font-circular">
            <div className="">
              <div className="flex items-center justify-between mb-4">
                <p className="font-medium font-circular text-padeSubtext">
                  Key Results ({totalEmployeeTargets || 0})
                </p>
                {!isPersonCentric && (
                  <div>
                    <Select
                      style={{ width: "150px", height: "100%" }}
                      options={filterOptions}
                      value={selectedStatus}
                      onChange={(value) => setSelectedStatus(value)}
                    />
                  </div>
                )}
              </div>

              {isPersonCentric ? (
                <KeyResultRowsList
                  data={personCentricKRs?.data}
                  isLoading={isLoadingPersonCentricKRs}
                  emptyText="No Key Result has been created or assigned yet"
                  role="admin"
                  enableActions
                  deleteEndpointBuilder={(kr) =>
                    `/api/v2/admin/okr/departmentkeyresult/${kr.employeeKeyResultId}`
                  }
                  onDeleteSuccess={() => {
                    queryClient.invalidateQueries({
                      queryKey: [
                        "/api/v2/admin/okr/okrframework/employees",
                        String(employeeId),
                        String(selectedCycle),
                        "keyresults",
                      ],
                    })
                    queryClient.invalidateQueries({
                      queryKey: [
                        `/api/v2/admin/okr/okrframework/employees/${employeeId}/cycledurations/${selectedCycle}/keyresults`,
                      ],
                    })
                  }}
                />
              ) : (
                <IndividualFlow
                  data={data || undefined}
                  // actionItems={actionItems}
                  setOpenCreatObj={setOpenCreateCompObj}
                  title="Individual"
                  emptyStateText={`No Key Result has been created or assigned yet`}
                  activeObj={activeObj}
                  setActiveObj={setActiveObj}
                  onDeleteKRSuccess={() => {
                    queryClient.invalidateQueries({
                      queryKey: ["/api/v2/admin/okr/companyobjectives"],
                    })
                  }}
                  delEndpoint="/api/v2/admin/okr/departmentkeyresult"
                  viewEndpoint={`/api/v2/admin/okr/employeekeyresults`}
                  selectedCycle={selectedCycle}
                  pageType="admin"
                />
              )}
            </div>
          </div>
        )}
      </DashboardWrapper>
    </>
  )
}

export default ViewIndividual
